// ============================================
// Landing page: "Reconstitution without the panic"
// Pain 2 (reconstitution anxiety, "did I ruin my vial?").
// Copy: docs/g2m-2026-07/02_landing_page_copy.md Page 4, with the
// CTA doctrine + honesty fixes from the G2M plan Section 5.
// ============================================

import React from 'react';
import { PainPage } from './PainPage';
import type { PainPageConfig } from './PainPage';

const config: PainPageConfig = {
  slug: 'reconstitution',
  path: '/reconstitution',
  metaTitle: 'Reconstitution Without the Panic | PRTCL',
  metaDescription:
    'How much bacteriostatic water? Did it foam? Is it cloudy? Did I ruin the vial? Learn to mix your peptide the right way, and use the free calculator to see exactly what your dose becomes.',
  eyebrow: 'MIX IT RIGHT, THE FIRST TIME',
  h1: (
    <>
      Reconstitution without <span className="lp-accent">the panic.</span>
    </>
  ),
  h1Text: 'Reconstitution without the panic.',
  sub: 'How much water? Did it foam? Is it cloudy? Did I ruin the vial? PRTCL walks you through mixing your peptide the right way and shows you exactly what your dose becomes.',
  heroCtaLabel: 'Get the free cheat sheet',
  secondary: { label: 'Or try the free calculator now', to: '/tools/reconstitution-calculator' },
  sections: [
    {
      heading: 'Name the fear',
      lead: '"Did I ruin my vial?" is the most common first-timer question there is.',
      body: (
        <>
          <p>
            Real people, mid-panic: "I just reconstituted for the first time and I think I might've
            messed up... It foamed up. Think it's still fine, or did I mess it up?" And: "Now I'm
            concerned that the potency might be affected." That knot in your stomach is normal. Let
            us untie it.
          </p>
        </>
      ),
      quote: {
        text: '"I\'m a bit lost when it comes to all the reconstitution processes. Is there a good resource available that outlines the dosage and reconstitution instructions?"',
        attribution: 'Community member, r/Retatrutide',
      },
    },
    {
      heading: 'The confusion',
      lead: 'Nobody agrees on how much water to add, and that is the whole problem.',
      body: (
        <>
          <p>
            One beginner asked the question everyone has: "If a vial lists X mg total, how do you
            determine how much bacteriostatic water to add? Why do people use different volumes of
            BAC water?" The internet gives ten different answers. The math gives you one clear one,
            matched to your vial.
          </p>
        </>
      ),
    },
    {
      heading: 'The promise',
      lead: 'Know how much water to add, and what each unit becomes.',
      body: (
        <>
          <p>
            Enter your vial into the free calculator on this site and see what each mark on your
            syringe is now worth. The full PRTCL app goes further: how to add the water without
            foaming, what a little cloudiness does or does not mean, and a step-by-step mixing
            walkthrough with pictures - so the "did I ruin it?" question never comes up.
          </p>
        </>
      ),
    },
  ],
  steps: [
    { title: 'Enter your vial size.', body: 'The calculator works with your exact vial and a clean, common water amount.' },
    { title: 'Follow the mixing steps.', body: 'Aim the water down the side of the glass. Let it dissolve. Do not shake.' },
    { title: 'See your dose math.', body: 'The calculator shows what each unit on your syringe now equals.' },
  ],
  reassurance: {
    heading: 'Most "did I ruin it?" moments end with "it was fine all along."',
    body: 'A little foam usually settles. Slight cloudiness is often normal while it dissolves. PRTCL tells you what actually matters and what you can stop worrying about.',
  },
  faqs: [
    {
      q: 'How much bacteriostatic water do I add?',
      a: 'It depends on your vial. The free calculator picks a simple, common amount for your vial size and shows the resulting dose math so the numbers stay easy.',
    },
    {
      q: 'It foamed / looks cloudy. Did I ruin it?',
      a: 'Usually not. Foam tends to settle and slight cloudiness while dissolving is often normal. The app explains what is normal, what is not, and when to slow down.',
    },
    {
      q: 'How long is it good for after mixing?',
      a: 'That depends on the peptide and storage. The free Storage and Safety guidance in the app gives plain timing and fridge guidance so you are not guessing.',
    },
    {
      q: 'Do I need the app to use the calculator?',
      a: 'No. The reconstitution calculator on this site is free and needs no account. The iOS app, free to download on the App Store, adds the guided mixing walkthrough with pictures.',
    },
    {
      q: 'Is this medical advice?',
      a: 'No. PRTCL and this calculator are educational. The math converts your own numbers and the guidance covers commonly reported technique. It is here to help you feel confident, not to replace your doctor. Always follow your own label and your provider.',
      defaultOpen: true,
    },
    {
      q: 'Where do I get the app?',
      a: 'PRTCL is on the App Store now, and the download is free. Tap the App Store button on this page to get it on your iPhone. Want the Reconstitution Cheat Sheet too? Drop your email below and it is yours. The calculator and Peptides 101 stay free on this site either way.',
      defaultOpen: true,
    },
  ],
  magnet: { name: 'Reconstitution Cheat Sheet', href: '/downloads/reconstitution-cheat-sheet.pdf' },
  magnetHeadline: 'Keep it by the fridge.',
  magnetLede:
    'Get the free Reconstitution Cheat Sheet: the mixing rules and the water-to-dose math for the most common vial sizes, on one page. You will also get short notes and new guides as we publish them. PRTCL itself is on the App Store now, free to download.',
};

const ReconstitutionPage: React.FC = () => <PainPage config={config} />;

export default ReconstitutionPage;
